/**
 * WineCard.js — Lightning Rock Winery wine listing card (cardType "wine").
 *
 * Pixel/behaviour parity with the old createWineListing card (lightningrockwinery): bottle image, serif red title, winery label,
 * Appellation / Varietal / Alcohol lines, a "Tasting notes" button (clipboard
 * icon) that opens the detailed view, price + "Per bottle", and the cart
 * control — "Add to cart" when the wine is not in the cart, or trash + qty
 * select + "Update" when it is.
 *
 * SCALABLE STRUCTURE:
 *   All cart mutations are events; features/optional/cart.js owns the BE calls.
 *   The card re-renders its action row on ws-cart-count so it flips between
 *   add / update states as the cart changes.
 *
 * EVENTS OUT:
 *   ws-cart-add {sku, quantity}
 *   ws-cart-update {sku, quantity}
 *   ws-cart-remove {sku}
 *   ws-prompt-select {label, value}   (Tasting notes / image → detail view)
 *
 * BE FIELDS (wine_listing): title, label, regular_price, price_unit, sold_out,
 *   available, variant_id, cover_image_link, wine_type, winemaker_notes
 *   { Appellation, Alcohol, Acid, pH }, message (LLM "tell me more" line).
 */

import { emit, on, getState ,isChatDisabled} from "../../../core/state.js";
import { bindWineDrawerOpen } from "../../../utils/openWineDrawer.js";

const MAX_QTY = 50; // old productMaxQty (script.js:188)

const TASTING_ICON =
  '<svg xmlns="http://www.w3.org/2000/svg" width="12" height="15" viewBox="0 0 12 15" fill="none"><path d="M3.16667 0.5V3.16667M5.83333 0.5V3.16667M8.5 0.5V3.16667M3.16667 5.83333H7.16667M3.16667 8.5H8.5M3.16667 11.1667H6.5M1.83333 1.83333H9.83333C10.5697 1.83333 11.1667 2.43029 11.1667 3.16667V12.5C11.1667 13.2364 10.5697 13.8333 9.83333 13.8333H1.83333C1.09695 13.8333 0.5 13.2364 0.5 12.5V3.16667C0.5 2.43029 1.09695 1.83333 1.83333 1.83333Z" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round"/></svg>';

const TRASH_ICON =
  '<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M2 4.00004H14M12.6667 4.00004V13.3334C12.6667 14 12 14.6667 11.3333 14.6667H4.66667C4 14.6667 3.33333 14 3.33333 13.3334V4.00004M5.33333 4.00004V2.66671C5.33333 2.00004 6 1.33337 6.66667 1.33337H9.33333C10 1.33337 10.6667 2.00004 10.6667 2.66671V4.00004" stroke="currentColor" stroke-width="1.33" stroke-linecap="round" stroke-linejoin="round"/></svg>';

function isTrue(v) {
  return v === true || v === "true" || v === "True";
}

function metaLine(wine) {
  const notes = wine.winemaker_notes || {};
  return [notes.Vintage, notes.Alcohol || notes.alcohol || "", wine.wine_type || ""]
    .filter(Boolean)
    .join(" | ");
}

export default class WineCard {
  render(wine) {
    const sku = wine.variant_id ?? wine["sku"] ?? "";
    const soldOut = isTrue(wine.sold_out);
    const unavailable = wine.available !== undefined && !isTrue(wine.available);
    const notes = wine.winemaker_notes || {};
    const detailLabel = wine.message || `Tell me more about ${wine.title || "this wine"}`;
    const openDetail = () => {

    if (isChatDisabled()) return;

    emit("ws-prompt-select", {
      label: detailLabel,
      value: detailLabel,
      uniqueId: wine.unique_id,
    });
  };
    const card = document.createElement("div");
    card.className = "ws-rich-winecard ws-lr-winecard";
    card.dataset.sku = sku;
    if (wine.unique_id != null) card.dataset.uniqueId = String(wine.unique_id);
    if (wine.wine_key) card.dataset.wineKey = String(wine.wine_key);

    // ── head: image (+ sold-out overlay) + text ──
    const head = document.createElement("div");
    head.className = "ws-lr-winecard-head";
    if (wine.cover_image_link) {
      const img = document.createElement("img");
      img.className = "ws-lr-winecard-img";
      img.src = wine.cover_image_link;
      img.alt = wine.title || "Wine";
      img.loading = "lazy";
      // In-stock: image opens detail. Sold-out: the whole card is clickable
      // (old createWineListing), so skip the image-level handler here.
         if (!soldOut) {
        img.style.cursor = "pointer";
        img.addEventListener("click", openDetail);
      }
      head.appendChild(img);
    }

    if (soldOut) {
      const overlay = document.createElement("div");
      overlay.className = "ws-lr-winecard-soldout";
      overlay.textContent = "SOLD OUT";
      head.appendChild(overlay);
    }

    card.appendChild(head);

    // ── body: tasting notes + price + action ──
    const body = document.createElement("div");
    body.className = "ws-lr-winecard-body";

    // Sold-out cards show price only — no tasting-notes button (old code);
    // the whole card is click-to-detail instead (see card click below).
    if (!soldOut) {
      const tasting = document.createElement("button");
      tasting.type = "button";
      tasting.className = "ws-lr-winecard-tasting";
      tasting.innerHTML = `${TASTING_ICON}<span>Tasting notes</span>`;
      bindWineDrawerOpen(tasting, wine, card);
      body.appendChild(tasting);
    }

    if (wine.title) {
      const title = document.createElement("p");
      title.className = "ws-rich-winetitle ws-lr-winecard-title";
      title.title = wine.title;
      title.textContent = wine.title;
      body.appendChild(title);
    }

    const meta = metaLine(wine);
    if (meta) {
      const desc = document.createElement("p");
      desc.className = "ws-lr-winecard-meta";
      desc.textContent = meta;
      body.appendChild(desc);
    }

    const details = document.createElement("div");
    details.className = "ws-lr-winecard-product-details";

    const footer = document.createElement("div");
    footer.className = "ws-lr-winecard-product-footer";

    const purchase = document.createElement("div");
    purchase.className = "ws-lr-winecard-purchase-row";

    if (wine.regular_price) {
      purchase.appendChild(this._line("ws-lr-price", wine.regular_price));
      // Lightning Rock Winery wines are sold per bottle. The old card always showed a price
      // unit (wine.price_unit); the new BE omits it for wines, so default to
      // "Per bottle". Lightning Rock Winery-only — this is the Lightning Rock Winery WineCard.
      purchase.appendChild(this._line("ws-lr-winecard-unit", wine?.bottle_quantity || "Per bottle"));
    }
    details.appendChild(footer);

    // Action row — only for purchasable wines. Flips add ↔ update on cart change.
    if (!soldOut && !unavailable && sku) {
      const actionMount = document.createElement("div");
      actionMount.className = "ws-lr-winecard-action-mount";
      const renderAction = () => {
        const line = (getState().cart.items || []).find(
          (it) => String(it.sku) === String(sku)
        );
        actionMount.replaceChildren(
          line ? this._buildUpdate(sku, Number(line.quantity) || 1) : this._buildAdd(sku, wine.cta_text)
        );
      };
      renderAction();
      on("ws-cart-count", () => {
        if (actionMount.isConnected) renderAction();
      });
      footer.appendChild(purchase);
      footer.appendChild(actionMount);

      // Spinner overlay over the whole card while a cart action runs — parity
      // with old overlayLoading(wineCard). Shown the moment an action button is
      // clicked; cleared when cart.js signals the mutation finished.
      const overlay = document.createElement("div");
      overlay.className = "ws-cart-overlay";
      overlay.hidden = true;
      card.appendChild(overlay);
      actionMount.addEventListener("click", (e) => {
        if (e.target.closest("button")) overlay.hidden = false;
      });
      on("ws-cart-busy", ({ active } = {}) => {
        if (!active && card.isConnected) overlay.hidden = true;
      });
    }

    body.appendChild(details);
    card.appendChild(body);

    // Sold-out: the entire card opens the detail view (old createWineListing
    // put the onclick on the .card wrapper). In-stock cards keep the image /
    // tasting-button handlers above instead.
    if (soldOut) {
      card.style.cursor = "pointer";
      card.addEventListener("click", (e) => {
          if (e.target.closest("a, button")) return;
          openDetail();
      });
    }

    return card;
  }

  _line(className, text, titleAttr) {
    const p = document.createElement("p");
    p.className = className;
    if (titleAttr) p.title = titleAttr;
    p.textContent = text;
    return p;
  }

  // Not in cart → solid "Add to cart" (adds one). aria-label/title use the
  // wine's cta_text (old createWineListing add-to-cart button).
  _buildAdd(sku, ctaText) {
    const btn = document.createElement("button");
    btn.type = "button";
    btn.className = "ws-lr-add";
    btn.textContent = "Add to cart";
    if (ctaText) {
      btn.setAttribute("aria-label", ctaText);
      btn.title = ctaText;
    }
    btn.addEventListener("click", () => emit("ws-cart-add", { sku, quantity: 1 }));
    return btn;
  }

  // In cart → trash + qty select (current selected) + "Update".
  _buildUpdate(sku, quantity) {
    const wrap = document.createElement("div");
    wrap.className = "ws-lr-product-action product-action";

    const del = document.createElement("button");
    del.type = "button";
    del.className = "ws-cp-delete delete-cart";
    del.setAttribute("aria-label", "delete from basket");
    del.title = "delete from basket";
    del.dataset.sku = sku;
    del.innerHTML = TRASH_ICON;
    del.addEventListener("click", () => emit("ws-cart-remove", { sku }));

    const qtyWrap = document.createElement("div");
    qtyWrap.className = "ws-cp-qty-wrap product-quantity-wrapper";

    const dec = document.createElement("button");
    dec.type = "button";
    dec.className = "ws-cp-dec dec-qty";
    dec.setAttribute("aria-label", "Decrease quantity");
    dec.innerHTML = '<span aria-hidden="true">−</span>';
    dec.addEventListener("click", () => {
      const next = Math.max(1, quantity - 1);
      if (next === quantity) return;
      emit("ws-cart-update", { sku, quantity: next });
    });

    const qty = document.createElement("div");
    qty.className = "ws-cp-cart-qty cart-qty";
    qty.textContent = String(quantity);

    const inc = document.createElement("button");
    inc.type = "button";
    inc.className = "ws-cp-inc inc-qty";
    inc.setAttribute("aria-label", "Increase quantity");
    inc.innerHTML = '<span aria-hidden="true">+</span>';
    inc.addEventListener("click", () =>
      emit("ws-cart-update", {
        sku,
        quantity: Math.min(12, quantity + 1),
      }),
    );

    qtyWrap.append(dec, qty, inc);
    wrap.append(del, qtyWrap);
    return wrap;
  }
}
